import { walkBlocks } from './document-state';
import type { HvyDocument } from './hvy-types';

const ACCESS_CODE_ID = 'notice-access-code';
const QR_CODE_ID = 'notice-qr-code';
const SURVEY_LINK_ID = 'notice-survey-link';
const NEIGHBORHOOD_PLACEHOLDER = 'NEIGHBORHOOD GOES HERE';

export interface NoticeBatchDocument {
  document: HvyDocument;
  pageBreakSectionIds: string[];
  surveyUrls: string[];
}

export function createNoticeBatchDocument(template: HvyDocument, neighborhood: string, codes: string[], questionCount = 13): NoticeBatchDocument {
  if (template.sections.length !== 1) throw new Error('The survey notice template must contain exactly one page section.');
  if (neighborhood.trim().length < 1 || neighborhood.length > 100) throw new Error('Survey notices require a neighborhood.');
  if (codes.length < 1 || codes.some((code) => !/^[A-Z0-9]{7}$/.test(code))) {
    throw new Error('Survey notices require one or more valid seven-character access codes.');
  }
  if (!Number.isInteger(questionCount) || questionCount < 1) throw new Error('Survey notices require a valid question count.');

  const templateDocument = structuredClone(template);
  const templateSection = templateDocument.sections[0];
  const templateBlocks = walkBlocks({ sections: [templateSection] });
  const qrTemplate = requireBlock(templateBlocks, QR_CODE_ID);
  requireBlock(templateBlocks, ACCESS_CODE_ID);
  requireBlock(templateBlocks, SURVEY_LINK_ID);
  if (!templateBlocks.some((block) => block.text.includes(NEIGHBORHOOD_PLACEHOLDER))) {
    throw new Error(`The survey notice template is missing the ${NEIGHBORHOOD_PLACEHOLDER} placeholder.`);
  }

  const surveyUrls = codes.map((code) => createSurveyUrl(qrTemplate.text, code));
  const sections = codes.map((code, index) => {
    const section = structuredClone(templateSection) as Record<string, unknown>;
    const sectionId = `survey-notice-${index + 1}`;
    section.key = sectionId;
    section.customId = sectionId;
    personalizeSection(section, neighborhood.trim(), code, surveyUrls[index]!, index + 1, questionCount);
    return section;
  });

  templateDocument.sections = sections;
  return {
    document: templateDocument,
    pageBreakSectionIds: sections.slice(1).map((section) => String(section.customId)),
    surveyUrls,
  };
}

function personalizeSection(section: Record<string, unknown>, neighborhood: string, code: string, surveyUrl: string, pageNumber: number, questionCount: number): void {
  const blocks = walkBlocks({ sections: [section] });
  requireBlock(blocks, ACCESS_CODE_ID).text = `## ${code}`;
  requireBlock(blocks, QR_CODE_ID).text = surveyUrl;
  requireBlock(blocks, SURVEY_LINK_ID).text = surveyUrl;
  const neighborhoodBlock = blocks.find((block) => block.text.includes(NEIGHBORHOOD_PLACEHOLDER))!;
  neighborhoodBlock.text = neighborhoodBlock.text.replace(NEIGHBORHOOD_PLACEHOLDER, neighborhood);
  const questionCountBlock = blocks.find((block) => /The survey is \d+ questions?/.test(block.text));
  if (!questionCountBlock) throw new Error('The survey notice template is missing the survey question count.');
  questionCountBlock.text = questionCountBlock.text.replace(/The survey is \d+ questions?/, `The survey is ${questionCount} ${questionCount === 1 ? 'question' : 'questions'}`);

  blocks.forEach((block, index) => {
    if (typeof block.id === 'string') block.id = `${block.id}-notice-${pageNumber}-${index + 1}`;
    if (block.schema.id) block.schema.id = `${block.schema.id}-notice-${pageNumber}`;
  });
  suffixNestedSectionIds(section, pageNumber);
}

function suffixNestedSectionIds(section: Record<string, unknown>, pageNumber: number): void {
  const children = Array.isArray(section.children) ? section.children : [];
  children.forEach((child, index) => {
    if (!child || typeof child !== 'object') return;
    const nested = child as Record<string, unknown>;
    nested.key = `${String(nested.key ?? 'section')}-notice-${pageNumber}-${index + 1}`;
    nested.customId = `${String(nested.customId ?? 'section')}-notice-${pageNumber}-${index + 1}`;
    suffixNestedSectionIds(nested, pageNumber);
  });
}

function createSurveyUrl(templateUrl: string, code: string): string {
  let url: URL;
  try {
    url = new URL(templateUrl.trim());
  } catch {
    throw new Error('The QR code in the survey notice template must contain a valid absolute URL.');
  }
  url.searchParams.delete('access-code');
  url.searchParams.set('accessCode', code);
  return url.toString();
}

function requireBlock(blocks: ReturnType<typeof walkBlocks>, id: string) {
  const block = blocks.find((candidate) => candidate.schema.id === id);
  if (!block) throw new Error(`The survey notice template is missing the ${id} field.`);
  return block;
}
